import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { exportComptableService } from '../../api/services';

const FORMATS: { value: 'CIEL_CSV' | 'SAGE_PNM' | 'FEC'; label: string; description: string }[] = [
  { value: 'CIEL_CSV', label: 'Ciel CSV', description: 'Format texte séparé par point-virgule, importable dans Ciel Compta et compatibles.' },
  { value: 'SAGE_PNM', label: 'SAGE PNM', description: 'Format positionné Sage 100 (lignes à largeur fixe).' },
  { value: 'FEC',      label: 'FEC',      description: 'Fichier des Écritures Comptables, format demandé par l\'administration fiscale (pipe-delimited).' },
];

const first = () => {
  const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};
const last = () => {
  const d = new Date(); d.setMonth(d.getMonth() + 1, 0); d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};

const ExportComptable: React.FC = () => {
  const [startDate, setStartDate] = useState(first());
  const [endDate, setEndDate] = useState(last());
  const [format, setFormat] = useState<'CIEL_CSV' | 'SAGE_PNM' | 'FEC'>('CIEL_CSV');
  const [busy, setBusy] = useState(false);

  const doExport = async () => {
    setBusy(true);
    try {
      const blob = await exportComptableService.export(startDate, endDate, format);
      const ext = format === 'CIEL_CSV' ? 'csv' : 'txt';
      const url = URL.createObjectURL(blob);
      const a = Object.assign(document.createElement('a'), {
        href: url,
        download: `export_${format.toLowerCase()}_${startDate}_${endDate}.${ext}`,
      });
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Export généré');
    } catch (e: any) {
      if (e?.response?.status === 402) {
        toast.error('Disponible sur le plan Pro ou Business. Mettez votre plan à niveau depuis Paramètres.');
      } else {
        toast.error(e?.response?.data?.message || 'Erreur export');
      }
    } finally { setBusy(false); }
  };

  const doExportExcel = async (type: 'clients' | 'articles' | 'factures' | 'paiements') => {
    setBusy(true);
    try {
      const blob = await exportComptableService.exportExcel(type);
      const url = URL.createObjectURL(blob);
      const a = Object.assign(document.createElement('a'), {
        href: url, download: `${type}.csv`,
      });
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Export généré');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur export');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Export comptable</h1>
        <p className="text-sm text-gray-500">Exporte vos écritures vers votre logiciel de comptabilité.</p>
      </div>

      <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-4 max-w-2xl">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Date de début</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                   className="w-full rounded-lg border border-gray-200 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Date de fin</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                   className="w-full rounded-lg border border-gray-200 px-3 py-2" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Format</label>
          <div className="space-y-2">
            {FORMATS.map((f) => (
              <label key={f.value} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${
                format === f.value ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
              }`}>
                <input type="radio" name="format" value={f.value} checked={format === f.value}
                       onChange={() => setFormat(f.value)} className="mt-1" />
                <div>
                  <div className="font-medium text-sm">{f.label}</div>
                  <div className="text-xs text-gray-500">{f.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
        <p className="text-xs text-gray-500">
          Chaque écriture du journal de vente porte le matricule fiscal du client
          (colonne <code>MATRICULE_FISCAL</code> en Ciel CSV, <code>MatriculeFiscal</code> en FEC,
          zone de fin de ligne en SAGE PNM). Il est vide pour les clients qui n'en ont pas de renseigné.
        </p>
        <button onClick={doExport} disabled={busy}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
          {busy ? 'Génération…' : 'Générer et télécharger'}
        </button>
      </div>

      <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-4 max-w-2xl">
        <div>
          <h2 className="font-semibold text-gray-900">Exports Excel</h2>
          <p className="text-sm text-gray-500">
            Téléchargez vos données au format CSV, ouvrable directement dans Excel (encodage UTF-8 :
            les libellés arabes et accentués s'affichent tels quels). Les exports clients, factures
            et paiements portent le matricule fiscal du client.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {(['clients', 'articles', 'factures', 'paiements'] as const).map((type) => (
            <button key={type} onClick={() => doExportExcel(type)} disabled={busy}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 capitalize">
              {type}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExportComptable;
